import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';
import { usePermissions } from '../../context/PermissionsContext';
import { PERMISSIONS } from '../../constants/permissions';
import {
  FolderOpen,
  Settings,
  Trash2,
  ChevronRight,
  LayoutGrid,
  List,
  Plus,
  Share2,
  UserRoundCog,
  Lock,
} from 'lucide-react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { Snackbar, Alert } from '@mui/material';
import KnowledgeBaseFolderDialog from '../KnowledgeBase/KnowledgeBaseFolderDialog';
import folderService from '../../services/folderService';
import folderD3Service from '../../services/folderD3Service';
import { getRootFolders, getChildFolders, isMirroredFolder } from '../../utils/folderTree';
import FolderDeleteDialogD1 from '../KnowledgeBaseView/FolderDeleteDialog';
import FolderConfigDialogD1 from '../KnowledgeBaseView/FolderConfigDialog';
import FolderDeleteDialogD3 from '../KnowledgeBaseViewD3/FolderDeleteDialog';
import FolderConfigDialogD3 from '../KnowledgeBaseViewD3/FolderConfigDialog';
import resourceSharingService from '../../services/resourceSharingService';
import kgDatasetService from '../../services/kgDatasetService';
import { unwrapList as unwrapKgList } from '../KnowledgeGraph/kgUtils';
import ResourceShareDialog from '../shared/ResourceShareDialog';
import ManageSharingDialog from '../shared/ManageSharingDialog';
import hubStyles from './KnowledgeBaseHub.module.scss';

/** @param {{ mode: 'd1' | 'd3' }} props */
export default function KnowledgeBaseHubFolderPanel({ mode }) {
  const { isDarkMode } = useTheme();
  const { has, ready: permissionsReady } = usePermissions();
  const canShareResources = permissionsReady && has(PERMISSIONS.RESOURCE_SHARING);
  const canUseKnowledgeGraph = permissionsReady && has(PERMISSIONS.KNOWLEDGE_GRAPH);
  const navigate = useNavigate();
  const FolderDeleteDialog = mode === 'd1' ? FolderDeleteDialogD1 : FolderDeleteDialogD3;
  const FolderConfigDialog = mode === 'd1' ? FolderConfigDialogD1 : FolderConfigDialogD3;

  const labels = useMemo(
    () =>
      mode === 'd1'
        ? {
            listSectionTitle: 'Rules book folders',
            emptyTitle: 'No rules book folders yet',
            emptySubtitle:
              'Create a folder to organize documents and processing settings for your company rules and SOPs.',
            loadFail: 'Failed to load folders',
            createFail: 'Failed to create knowledge base folder. Please try again.',
            deleteFail: 'Failed to delete folder. Please try again.',
            toastCreateOk: (name) => `Knowledge base folder "${name}" created successfully!`,
            toastDeleteOk: (name) => `Folder "${name}" deleted successfully!`,
          }
        : {
            listSectionTitle: 'Customer data folders',
            emptyTitle: 'No customer data folders yet',
            emptySubtitle:
              'Create a folder to organize customer data and context for your agents.',
            loadFail: 'Failed to load D3 folders',
            createFail: 'Failed to create D3 knowledge base folder. Please try again.',
            deleteFail: 'Failed to delete D3 folder. Please try again.',
            toastCreateOk: (name) => `D3 Knowledge base folder "${name}" created successfully!`,
            toastDeleteOk: (name) => `D3 Folder "${name}" deleted successfully!`,
          },
    [mode]
  );

  const viewModeKey = mode === 'd1' ? 'knowledgeBaseViewMode' : 'knowledgeBaseViewD3Mode';

  const muiTheme = createTheme({
    palette: {
      mode: isDarkMode ? 'dark' : 'light',
      primary: {
        main: '#e36250 !important',
      },
      secondary: {
        main: '#8b5cf6',
      },
    },
  });

  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState({ open: false, message: '', severity: 'info' });
  const [folders, setFolders] = useState([]);
  const [showFolderDialog, setShowFolderDialog] = useState(false);
  const [isCreatingFolder, setIsCreatingFolder] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    folder: null,
    isDeleting: false,
  });
  const [configDialog, setConfigDialog] = useState({
    open: false,
    folder: null,
  });
  const [shareFolder, setShareFolder] = useState(null);
  const [manageFolder, setManageFolder] = useState(null);
  // Creating a Knowledge Graph dataset creates a D3 folder to hold its records,
  // which then shows up in this list looking like any other folder. Without a
  // label it is a mystery folder someone will try to upload files into — so map
  // folder_id → dataset and badge them.
  const [kgFoldersById, setKgFoldersById] = useState({});

  const [viewMode, setViewMode] = useState(() => localStorage.getItem(viewModeKey) || 'list');

  // D3 folders nest via parentFolderId — show only top-level folders here; subfolders
  // live inside their parent. D1 folders are always flat.
  const displayedFolders = useMemo(
    () => (mode === 'd3' ? getRootFolders(folders) : folders),
    [mode, folders],
  );

  // A Knowledge Graph dataset owns a D3 folder to hold its records, but that
  // folder is an implementation detail and a dead end here: its contents are
  // pushed records chunked into the vector store, never files, so the folder
  // page shows an empty list and offers Upload — none of which applies. It
  // belongs on the Knowledge Graph screens.
  const hiddenKgFolders = useMemo(
    () => displayedFolders.filter((f) => kgFoldersById[String(f.id)]),
    [displayedFolders, kgFoldersById],
  );
  const visibleFolders = useMemo(
    () => displayedFolders.filter((f) => !kgFoldersById[String(f.id)]),
    [displayedFolders, kgFoldersById],
  );

  const setViewModePersist = (next) => {
    setViewMode(next);
    localStorage.setItem(viewModeKey, next);
  };

  const showToast = useCallback((message, severity = 'info') => {
    setToast({ open: true, message, severity });
  }, []);

  const loadFolders = useCallback(async () => {
    try {
      const [ownedResult, sharedResult] = await Promise.all([
        mode === 'd1' ? folderService.getFolders() : folderD3Service.getFolders(),
        resourceSharingService.getSharedWithMe(mode === 'd1' ? 'folder_dim1' : 'folder_dim3').catch(() => ({ items: [] })),
      ]);
      const owned = Array.isArray(ownedResult) ? ownedResult : [];
      const shared = Array.isArray(sharedResult?.items) ? sharedResult.items.map((item) => ({
        ...(item.resource || {}),
        id: item.resource_id ?? item.resource?.id,
        __shared: true,
        __sharePermission: item.permission || 'read',
        __shareOwner: item.owner,
        owner_user_id: item.owner?.id,
      })) : [];
      setFolders([...owned, ...shared]);
    } catch (error) {
      console.error('Error loading folders:', error);
      setFolders([]);
      showToast(labels.loadFail, 'error');
    }
  }, [mode, labels.loadFail, showToast]);

  // Only D3 folders can belong to a dataset, and only for someone who has the
  // permission — otherwise nothing extra is fetched at all.
  const loadKgFolders = useCallback(async () => {
    if (mode !== 'd3' || !canUseKnowledgeGraph) return;
    try {
      const response = await kgDatasetService.listDatasets();
      const datasets = unwrapKgList(response, 'datasets', 'kg_datasets');
      const map = {};
      datasets.forEach((dataset) => {
        if (dataset?.folder_id !== undefined && dataset?.folder_id !== null) {
          map[String(dataset.folder_id)] = dataset;
        }
      });
      setKgFoldersById(map);
    } catch (error) {
      // Feature disabled or no access — folders just render unbadged.
      setKgFoldersById({});
    }
  }, [mode, canUseKnowledgeGraph]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      await Promise.all([loadFolders(), loadKgFolders()]);
    } catch (error) {
      console.error('Error loading data:', error);
      showToast(labels.loadFail, 'error');
    } finally {
      setLoading(false);
    }
  }, [loadFolders, loadKgFolders, labels.loadFail, showToast]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleToastClose = () => {
    setToast({ open: false, message: '', severity: 'info' });
  };

  const handleCreateFolder = async (folderConfig) => {
    setIsCreatingFolder(true);
    try {
      if (mode === 'd1') {
        await folderService.createFolder(folderConfig);
      } else {
        await folderD3Service.createFolder(folderConfig);
      }
      showToast(labels.toastCreateOk(folderConfig.name), 'success');
      setShowFolderDialog(false);
      loadFolders();
    } catch (error) {
      console.error('Error creating folder:', error);
      showToast(labels.createFail, 'error');
    } finally {
      setIsCreatingFolder(false);
    }
  };

  const handleOpenFolderDialog = () => setShowFolderDialog(true);
  const handleCloseFolderDialog = () => setShowFolderDialog(false);

  const handleFolderClick = (folder) => {
    const sharedQuery = folder.__shared
      ? `?shared=true&permission=${encodeURIComponent(folder.__sharePermission)}&owner_user_id=${encodeURIComponent(folder.owner_user_id || '')}`
      : '';
    if (mode === 'd1') {
      navigate(`/knowledge-base/folder/${folder.id}${sharedQuery}`);
    } else {
      navigate(`/knowledge-base-d3/folder/${folder.id}${sharedQuery}`);
    }
  };

  const handleDeleteFolder = (folder) => {
    setDeleteDialog({ open: true, folder, isDeleting: false });
  };

  const handleConfirmDelete = async () => {
    if (!deleteDialog.folder) return;
    setDeleteDialog((prev) => ({ ...prev, isDeleting: true }));
    try {
      if (mode === 'd1') {
        await folderService.deleteFolder(deleteDialog.folder.id);
      } else {
        await folderD3Service.deleteFolder(deleteDialog.folder.id);
      }
      showToast(labels.toastDeleteOk(deleteDialog.folder.name), 'success');
      setDeleteDialog({ open: false, folder: null, isDeleting: false });
      loadFolders();
    } catch (error) {
      console.error('Error deleting folder:', error);
      showToast(labels.deleteFail, 'error');
      setDeleteDialog((prev) => ({ ...prev, isDeleting: false }));
    }
  };

  const handleCancelDelete = () => {
    setDeleteDialog({ open: false, folder: null, isDeleting: false });
  };

  const handleShowConfig = (folder) => {
    setConfigDialog({ open: true, folder });
  };

  const handleCloseConfig = () => {
    setConfigDialog({ open: false, folder: null });
  };

  return (
    <ThemeProvider theme={muiTheme}>
      <div>
        <div className={hubStyles.listHeader}>
          <div className={hubStyles.listHeaderLeft}>
            <h2 className={hubStyles.listTitle}>{labels.listSectionTitle}</h2>
          </div>
          <span className={hubStyles.countLabel}>
            {loading ? 'Loading…' : `${visibleFolders.length} ${visibleFolders.length === 1 ? 'folder' : 'folders'}`}
          </span>
        </div>

        {!loading && (
          <div className={hubStyles.folderPanelToolbar}>
            <p className={hubStyles.folderToolbarHint}>
              View and manage folders. Open a folder to upload files or adjust settings on the full page.
              {hiddenKgFolders.length > 0 && (
                <>
                  {' '}
                  {hiddenKgFolders.length} folder{hiddenKgFolders.length === 1 ? ' is' : 's are'}{' '}
                  managed by{' '}
                  <button
                    type="button"
                    className={hubStyles.inlineLink}
                    onClick={() => navigate('/knowledge-graph')}
                  >
                    Knowledge Graph
                  </button>{' '}
                  and not shown here.
                </>
              )}
            </p>
            <div className={hubStyles.folderToolbarRight}>
              <div className={hubStyles.folderViewToggle} role="group" aria-label="View mode">
                <button
                  type="button"
                  className={`${hubStyles.folderViewBtn} ${viewMode === 'list' ? hubStyles.folderViewBtnActive : ''}`}
                  onClick={() => setViewModePersist('list')}
                  aria-pressed={viewMode === 'list'}
                >
                  <List size={16} strokeWidth={2} aria-hidden />
                  List
                </button>
                <button
                  type="button"
                  className={`${hubStyles.folderViewBtn} ${viewMode === 'card' ? hubStyles.folderViewBtnActive : ''}`}
                  onClick={() => setViewModePersist('card')}
                  aria-pressed={viewMode === 'card'}
                >
                  <LayoutGrid size={16} strokeWidth={2} aria-hidden />
                  Cards
                </button>
              </div>
              <button type="button" className={hubStyles.uploadBtn} onClick={handleOpenFolderDialog}>
                <Plus size={16} />
                Create folder
              </button>
            </div>
          </div>
        )}

        {loading ? (
          <div className={`${hubStyles.emptyState} ${hubStyles.muted}`}>
            <div className={hubStyles.emptyIcon}>
              <FolderOpen size={32} strokeWidth={1.5} />
            </div>
            <p>Loading folders…</p>
          </div>
        ) : visibleFolders.length === 0 ? (
          <div className={hubStyles.emptyState}>
            <div className={hubStyles.emptyIcon}>
              <FolderOpen size={32} strokeWidth={1.5} />
            </div>
            <p style={{ fontWeight: 700, color: 'var(--text-primary)', marginBottom: 8 }}>{labels.emptyTitle}</p>
            <p className={hubStyles.emptyFolderCallout}>{labels.emptySubtitle}</p>
            <button type="button" className={hubStyles.uploadBtn} onClick={handleOpenFolderDialog}>
              <Plus size={16} />
              Create first folder
            </button>
          </div>
        ) : viewMode === 'card' ? (
          <div className={hubStyles.folderCardGrid}>
            {visibleFolders.map((folder) => (
              <div key={folder.id} className={hubStyles.folderHubCard}>
                <div className={hubStyles.folderHubCardTop}>
                  <div className={hubStyles.folderHubCardTitleRow}>
                    <div className={hubStyles.folderHubCardIcon}>
                      <FolderOpen size={20} strokeWidth={2} />
                    </div>
                    <div>
                      <h3 className={hubStyles.folderHubCardName}>{folder.name}</h3>
                      <p className={hubStyles.folderHubCardDate}>
                        Created {new Date(folder.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <div className={hubStyles.folderHubCardActions}>
                    {!folder.__shared && canShareResources && <button
                      type="button" className={hubStyles.iconBtn} title="Share folder" aria-label="Share folder"
                      onClick={() => setShareFolder(folder)}
                    ><Share2 size={16} /></button>}
                    {!folder.__shared && canShareResources && <button
                      type="button" className={hubStyles.iconBtn} title="Manage sharing" aria-label="Manage sharing"
                      onClick={() => setManageFolder(folder)}
                    ><UserRoundCog size={16} /></button>}
                    {!folder.__shared && <button
                      type="button"
                      className={hubStyles.iconBtn}
                      title="Configuration"
                      aria-label="Configuration"
                      onClick={() => handleShowConfig(folder)}
                    >
                      <Settings size={16} />
                    </button>}
                    {!folder.__shared && <button
                      type="button"
                      className={`${hubStyles.iconBtn} ${hubStyles.danger}`}
                      title="Delete folder"
                      aria-label="Delete folder"
                      onClick={() => handleDeleteFolder(folder)}
                    >
                      <Trash2 size={16} />
                    </button>}
                  </div>
                </div>
                {typeof folder.description === 'string' && folder.description.trim() ? (
                  <p className={hubStyles.folderHubCardDesc}>{folder.description.trim()}</p>
                ) : null}
                {mode === 'd3' && (isMirroredFolder(folder) || getChildFolders(folders, folder.id).length > 0) && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
                    {isMirroredFolder(folder) && (
                      <span className={hubStyles.folderChip} style={{ color: '#8b5cf6', borderColor: '#8b5cf6' }}>
                        Synced from Google Drive
                      </span>
                    )}
                    {getChildFolders(folders, folder.id).length > 0 && (
                      <span className={hubStyles.folderChip}>
                        {getChildFolders(folders, folder.id).length} subfolder
                        {getChildFolders(folders, folder.id).length === 1 ? '' : 's'}
                      </span>
                    )}
                  </div>
                )}
                <div className={hubStyles.folderHubCardFooter}>
                  <span className={`${hubStyles.folderChip} ${hubStyles.folderChipOk}`}>
                    {folder.__shared ? `Shared by ${folder.__shareOwner?.name || folder.__shareOwner?.email || 'teammate'}` : 'Active'}
                  </span>
                  <button
                    type="button"
                    className={hubStyles.viewAgentBtn}
                    onClick={() => handleFolderClick(folder)}
                  >
                    View files
                    <ChevronRight size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div>
            {visibleFolders.map((folder) => (
              <div
                key={folder.id}
                role="button"
                tabIndex={0}
                className={hubStyles.folderRow}
                onClick={() => handleFolderClick(folder)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleFolderClick(folder);
                  }
                }}
              >
                <div className={hubStyles.folderRowMain}>
                  <div className={hubStyles.folderAvatar} aria-hidden>
                    <FolderOpen size={22} strokeWidth={2} />
                  </div>
                  <div className={hubStyles.fileMeta}>
                    <h3>{folder.name}</h3>
                    {mode === 'd1' ? (
                      typeof folder.description === 'string' && folder.description.trim() ? (
                        <p className={hubStyles.desc}>{folder.description.trim()}</p>
                      ) : null
                    ) : (
                      <div className={hubStyles.subRow}>
                        <span>Created {new Date(folder.created_at).toLocaleDateString()}</span>
                        {isMirroredFolder(folder) && (
                          <>
                            <span>·</span>
                            <span style={{ color: '#8b5cf6' }}>Synced from Google Drive</span>
                          </>
                        )}
                        {getChildFolders(folders, folder.id).length > 0 && (
                          <>
                            <span>·</span>
                            <span>
                              {getChildFolders(folders, folder.id).length} subfolder
                              {getChildFolders(folders, folder.id).length === 1 ? '' : 's'}
                            </span>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                <div className={hubStyles.folderRowActions}>
                  <span className={`${hubStyles.folderChip} ${hubStyles.folderChipOk}`}>
                    {folder.__shared ? <><Lock size={12} /> {folder.__sharePermission === 'write' ? 'Shared · can edit files' : 'Shared · read only'}</> : 'Active'}
                  </span>
                  {!folder.__shared && canShareResources && <button
                    type="button" className={hubStyles.iconBtn} title="Share folder" aria-label="Share folder"
                    onClick={(e) => { e.stopPropagation(); setShareFolder(folder); }}
                  ><Share2 size={16} /></button>}
                  {!folder.__shared && canShareResources && <button
                    type="button" className={hubStyles.iconBtn} title="Manage sharing" aria-label="Manage sharing"
                    onClick={(e) => { e.stopPropagation(); setManageFolder(folder); }}
                  ><UserRoundCog size={16} /></button>}
                  {!folder.__shared && <button
                    type="button"
                    className={hubStyles.iconBtn}
                    title="Configuration"
                    aria-label="Configuration"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleShowConfig(folder);
                    }}
                  >
                    <Settings size={16} />
                  </button>}
                  {!folder.__shared && <button
                    type="button"
                    className={`${hubStyles.iconBtn} ${hubStyles.danger}`}
                    title="Delete folder"
                    aria-label="Delete folder"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteFolder(folder);
                    }}
                  >
                    <Trash2 size={16} />
                  </button>}
                  <button
                    type="button"
                    className={hubStyles.viewAgentBtn}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleFolderClick(folder);
                    }}
                  >
                    View files
                    <ChevronRight size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        <KnowledgeBaseFolderDialog
          open={showFolderDialog}
          onClose={handleCloseFolderDialog}
          onCreateFolder={handleCreateFolder}
          isCreating={isCreatingFolder}
        />

        <FolderDeleteDialog
          open={deleteDialog.open}
          onClose={handleCancelDelete}
          onConfirm={handleConfirmDelete}
          folder={deleteDialog.folder}
          isDeleting={deleteDialog.isDeleting}
        />

        <FolderConfigDialog
          open={configDialog.open}
          onClose={handleCloseConfig}
          folder={configDialog.folder}
        />

        <ResourceShareDialog
          open={Boolean(shareFolder)}
          resource={shareFolder ? { type: mode === 'd1' ? 'folder_dim1' : 'folder_dim3', id: shareFolder.id, name: shareFolder.name } : null}
          onClose={() => setShareFolder(null)}
          onSuccess={(result) => {
            const warnings = Array.isArray(result?.warnings) ? result.warnings : [];
            showToast(warnings.length ? `Folder shared. ${warnings.join(' ')}` : 'Folder shared successfully.', warnings.length ? 'warning' : 'success');
          }}
        />
        <ManageSharingDialog
          open={Boolean(manageFolder)}
          resource={manageFolder ? { type: mode === 'd1' ? 'folder_dim1' : 'folder_dim3', id: manageFolder.id, name: manageFolder.name } : null}
          onClose={() => setManageFolder(null)}
          onChanged={loadFolders}
        />

        <Snackbar
          open={toast.open}
          autoHideDuration={4000}
          onClose={handleToastClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert onClose={handleToastClose} severity={toast.severity} sx={{ width: '100%' }}>
            {toast.message}
          </Alert>
        </Snackbar>
      </div>
    </ThemeProvider>
  );
}
